import { Plus, BookOpen, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

function ChapterSidebar({ chapters, currentChapterId, bookTitle, onChapterSelect, onAddChapter }) {
  return (
    <div 
      className="bg-[#FFFCF7]/70 dark:bg-[#211E1A]/70 backdrop-blur-sm border border-[#E7DED1]/60 dark:border-[#302820]/60 rounded-xl overflow-hidden h-full flex flex-col"
      data-testid="chapter-sidebar"
    >
      {/* Book Title */}
      <div className="px-4 py-3 border-b border-[#ECE3D7]/65 dark:border-[#302820]/60">
        <div className="flex items-center space-x-2">
          <BookOpen className="w-4 h-4 text-[#756958] dark:text-[#B8AEA4]" />
          <span className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] text-sm truncate">
            {bookTitle}
          </span>
        </div>
      </div>

      {/* Chapters List */}
      <div className="flex-1 overflow-y-auto py-2">
        {chapters.map((chapter, index) => {
          const isActive = chapter.chapter_id === currentChapterId;
          const hasContent = chapter.content && chapter.content.length > 0;
          const wordCount = chapter.content ? chapter.content.trim().split(/\s+/).filter(Boolean).length : 0;
          
          return (
            <button
              key={chapter.chapter_id}
              onClick={() => onChapterSelect(chapter.chapter_id)}
              data-testid={`chapter-nav-${index + 1}`}
              className={`w-full px-4 py-2.5 flex items-center space-x-3 transition-colors ${
                isActive
                  ? 'bg-[#F4EEF8]/80 dark:bg-[#2B2430]/78 text-[#2A2420] dark:text-[#F0EBE4]'
                  : 'hover:bg-[#F7F1E9] dark:hover:bg-[#29241F]/70 text-[#2A2420] dark:text-[#F0EBE4]'
              }`}
            >
              {/* Chapter Number Circle */}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                isActive
                  ? 'bg-white dark:bg-[#211E1A] text-[#5C4D6B] dark:text-[#D7C8E6] border border-[#D9C9E3] dark:border-[#6B5578]'
                  : hasContent
                    ? 'bg-[#EAF3EC] dark:bg-[#203027] text-[#2F6F5E] dark:text-[#8AC5B4]'
                    : 'bg-[#F7F1E9] dark:bg-[#29241F] text-[#8C8176] dark:text-[#B8AEA4]'
              }`}>
                {hasContent ? (
                  <Check className="w-3 h-3" />
                ) : (
                  index + 1
                )}
              </div>

              {/* Chapter Info */}
              <div className="flex-1 text-left min-w-0">
                <p className={`text-sm font-medium truncate ${
                  isActive ? '' : ''
                }`}>
                  {chapter.title || `Chapter ${index + 1}`}
                </p>
                {hasContent && (
                  <p className={`text-xs ${
                    isActive 
                      ? 'text-[#5C4D6B]/70 dark:text-[#D7C8E6]/70'
                      : 'text-[#2A2420]/50 dark:text-[#F0EBE4]/50'
                  }`}>
                    {wordCount.toLocaleString()} words
                  </p>
                )}
              </div>

              {/* Active Indicator */}
              {isActive && (
                <div className="w-1.5 h-1.5 bg-[#5C4D6B] dark:bg-[#D7C8E6] rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Add Chapter Button */}
      <div className="p-3 border-t border-[#ECE3D7]/65 dark:border-[#302820]/60">
        <Button
          onClick={onAddChapter}
          variant="outline"
          size="sm"
          data-testid="add-chapter-sidebar-btn"
          className="w-full rounded-full border-[#D8CBBC] bg-white/50 text-[#2A2420] hover:bg-[#F7F1E9] dark:border-[#3A332C] dark:bg-[#211E1A] dark:text-[#F0EBE4] dark:hover:bg-[#29241F]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Chapter
        </Button>
      </div>
    </div>
  );
}

export default ChapterSidebar;
